/**
 * ============================================================================
 * ⭐ THE DETENTION CLOCK (F103, Decision 280)
 * ============================================================================
 *
 * > Pete, 2026-09-28: *"2 hour turn is called detention I think. We definitely
 * > need to track that on trucks"* → *"Live on the truck + stored"*.
 *
 * ⭐ WHEN THE CLOCK STARTS — the usual carrier contract term: **the later of the
 * truck's arrival and its booked start.** A truck that turns up two hours early
 * for a 10:00 slot has not been detained at 9:00; one that arrives at 10:20 for
 * a 10:00 slot starts its clock at 10:20. A day-only booking has no promised
 * time, so its clock starts at arrival.
 *
 * ⭐ WHEN IT STOPS — departure. While the truck is on site the clock runs to now.
 *
 * ⭐ THE SETTINGS COME FROM THE SERVER (`dock.FacilityConfig`, migrations 224 + 229:
 * free time, default 60; orange lead, default 30; red lead, default 15). ⛔ This file
 * holds NO default: no settings means no colouring, never a guessed hour.
 *
 * ⭐ F155 (Decision 329, 2026-09-29) — IT COUNTS DOWN.
 * > Pete: *"the detention clock should start at his appointment time and count down
 * > from there. Should only be 1-hour to unload/load the truck. Turn orange at 30 and
 * > red at 15"* → *"Thats on the dock scheduler app to"* → popups: a late driver's
 * > clock starts *"At arrival"*; at zero *"Count up 'over', red"*; above 30 minutes
 * > left, *"Same color as the status - arrived."*
 *   ok      more than the orange lead left   → the ARRIVED status colour
 *   warn    orange lead or less left          → orange
 *   urgent  red lead or less left             → red
 *   over    free time used up                 → red, and the clock counts UP "over"
 * `detentionLabel` is the one place the words are made: "45m left" / "12m over".
 *
 * ⛔ ONE RULE. The card uses this; the stored measure the engine writes
 * (KPI spec §8 B7 / L3) uses the same definition, written down in the spec.
 */

export interface DetentionSettings {
    /** Free minutes before detention. 0 = the clock is off at this facility. */
    freeMinutes: number;
    /** ORANGE this many minutes before the free time runs out. 0 = no orange. */
    warnMinutes: number;
    /** RED this many minutes before the free time runs out. 0 / absent = red only once over. */
    urgentMinutes?: number;
    /**
     * ⭐ F173 (Decision 344, migration 231) — client free times. The server sends only the
     * rules that can apply at THIS facility (its company's, for this facility or every
     * facility), so `settingsFor` needs no facility of its own to match on.
     */
    rules?: readonly DetentionRule[];
}

/** ⭐ F173 — one client's free time. `direction` null = both; `facilityCode` null = every facility. */
export interface DetentionRule {
    clientCode: string;
    direction: 'INBOUND' | 'OUTBOUND' | null;
    facilityCode: string | null;
    freeMinutes: number;
}

/** The appointment's direction in the rule's words (the board says load / unload). */
function ruleDirection(direction: string | null | undefined): 'INBOUND' | 'OUTBOUND' | null {
    const d = (direction ?? '').trim().toUpperCase();
    if (d === 'INBOUND' || d === 'UNLOAD') return 'INBOUND';
    if (d === 'OUTBOUND' || d === 'LOAD') return 'OUTBOUND';
    return null;
}

/**
 * ⭐⭐ F173 — THE SETTINGS FOR ONE TRUCK. Pete: *"build the detention free timer per client
 * and direction with a default for everyone that doesn't have a record at 2hours"*.
 * The most specific rule for the truck's client replaces the facility's free time;
 * orange and red leads stay the facility's. Weights (the client must match; a rule's
 * direction must match when it has one):
 *   client + direction + facility (3) > client + direction (2) > client + facility (1) > client (0)
 * No rule → the facility's own settings, unchanged (and its default is the server's 120).
 * ⛔ Same weights as the server's `resolveFreeMinutes`; detention-guard RUNS both.
 */
export function settingsFor(
    settings: DetentionSettings | null, clientCode: string | null | undefined, direction: string | null | undefined,
): DetentionSettings | null {
    if (!settings || !settings.rules || settings.rules.length === 0) return settings;
    const c = (clientCode ?? '').trim().toLowerCase();
    if (!c) return settings;
    const d = ruleDirection(direction);
    let best: DetentionRule | null = null;
    let bestScore = -1;
    for (const r of settings.rules) {
        if (r.clientCode.trim().toLowerCase() !== c) continue;
        if (r.direction !== null && r.direction !== d) continue;
        const score = (r.direction !== null ? 2 : 0) + (r.facilityCode !== null ? 1 : 0);
        if (score > bestScore) { best = r; bestScore = score; }
    }
    return best ? { ...settings, freeMinutes: best.freeMinutes } : settings;
}

export type DetentionLevel = 'off' | 'ok' | 'warn' | 'urgent' | 'over';

export interface DetentionInput {
    /** The appointment's recorded arrival, else the kiosk check-in time. */
    arrivalIso: string | null;
    /** The booked start; ignored for a day-only booking. */
    scheduledStartIso: string | null;
    /** False for a day-only booking (no promised time). */
    timed: boolean;
    /** Recorded departure, or null while on site. */
    departureIso: string | null;
}

export interface DetentionState {
    level: DetentionLevel;
    /** When the detention clock started (ISO). */
    clockStartIso: string;
    /** Minutes on the detention clock (start → departure or now). */
    clockMinutes: number;
    /** Minutes since arrival (what the driver feels). */
    onSiteMinutes: number;
    /** Minutes until detention starts (negative once in detention); null when off. */
    minutesLeft: number | null;
    /** Minutes past the free time (0 until then). */
    overMinutes: number;
}

const ms = (iso: string | null): number | null => {
    if (!iso) return null;
    const t = new Date(iso).getTime();
    return Number.isFinite(t) ? t : null;
};

export function detentionState(
    input: DetentionInput, settings: DetentionSettings | null, now: number,
): DetentionState | null {
    const arrival = ms(input.arrivalIso);
    if (arrival === null) return null;
    const booked = input.timed ? ms(input.scheduledStartIso) : null;
    const start = booked !== null && booked > arrival ? booked : arrival;
    const end = ms(input.departureIso) ?? now;
    const clockMinutes = Math.max(0, Math.floor((end - start) / 60_000));
    const onSiteMinutes = Math.max(0, Math.floor((end - arrival) / 60_000));
    const base = { clockStartIso: new Date(start).toISOString(), clockMinutes, onSiteMinutes };

    if (!settings || !(settings.freeMinutes > 0)) {
        return { ...base, level: 'off', minutesLeft: null, overMinutes: 0 };
    }
    const minutesLeft = settings.freeMinutes - clockMinutes;
    const urgent = settings.urgentMinutes ?? 0;
    let level: DetentionLevel = 'ok';
    if (minutesLeft <= 0) level = 'over';
    else if (urgent > 0 && minutesLeft <= urgent) level = 'urgent';
    else if (settings.warnMinutes > 0 && minutesLeft <= settings.warnMinutes) level = 'warn';
    return { ...base, level, minutesLeft, overMinutes: Math.max(0, -minutesLeft) };
}

/** Red on every screen: the red lead, and detention itself. */
export const isDetentionRed = (level: DetentionLevel): boolean => level === 'urgent' || level === 'over';

/**
 * ⭐ F155 — the words on the clock: "45m left" counting down, then "12m over" counting up.
 * ⭐ F159 — `minutesOnly` writes "60m left" instead of "1h 00m left": the driver card's
 * facts row has no room for the longer form (Pete: *"DD4x is off the page"*). The wall
 * board keeps hours and minutes.
 */
export function detentionLabel(st: DetentionState, opts: { minutesOnly?: boolean } = {}): string {
    const fmt = (n: number) => (opts.minutesOnly ? `${Math.max(0, Math.round(n))}m` : formatMinutes(n));
    if (st.minutesLeft === null) return fmt(st.clockMinutes);
    return st.level === 'over' ? `${fmt(st.overMinutes)} over` : `${fmt(st.minutesLeft)} left`;
}

/** "2h 05m" / "47m". */
export function formatMinutes(mins: number): string {
    const m = Math.max(0, Math.round(mins));
    const h = Math.floor(m / 60);
    return h > 0 ? `${h}h ${String(m % 60).padStart(2, '0')}m` : `${m}m`;
}
